/**
 * API-Facade — einzige Stelle fuer HTTP-Zugriffe (Muster netzfabriks-erp).
 *
 * Datenzugriff im UI ausschliesslich via TanStack Query ueber diese Facade;
 * ein Namespace pro Domain (api.employees.…, api.attendance.…).
 */

import axios, { AxiosError } from "axios"
import { AUTH_MODE, getAccessToken, getDevUser, triggerRelogin } from "@/store/auth"
import type {
  Absence,
  AbsenceConflict,
  AbsenceCreate,
  AbsenceListParams,
  AbsenceUpdate,
  ApplyClosureRequest,
  ApplyClosureResponse,
  Assignment,
  AssignmentCreate,
  AssignmentListParams,
  AssignmentType,
  AssignmentTypeCreate,
  AssignmentTypeUpdate,
  AssignmentUpdate,
  AttendanceAdminUpdate,
  AttendanceReportParams,
  AttendanceReportResponse,
  AttendanceBoard,
  AttendanceListParams,
  AttendanceRecord,
  BulkAbsenceRequest,
  BulkAbsenceResponse,
  BulkDeleteParams,
  BulkDeleteResponse,
  CheckInRequest,
  CheckOutRequest,
  CalendarEntry,
  CalendarEntryCreate,
  CalendarEntryListParams,
  CalendarEntryUpdate,
  CalendarYear,
  ClosureRangeRequest,
  ClosureRangeResult,
  CostCenter,
  CostCenterCreate,
  CostCenterUpdate,
  DashboardResponse,
  DayInfos,
  DocumentCreate,
  DocumentItem,
  DocumentListParams,
  DocumentUpdate,
  EmployeeAddress,
  EmployeeAddressCreate,
  EmployeeAddressUpdate,
  EmployeeCreate,
  EmployeeDetail,
  EmployeeEmail,
  EmployeeEmailCreate,
  EmployeeEmailUpdate,
  EmployeeListItem,
  EmployeeListParams,
  EmployeePositionCreate,
  EmployeePositionEntry,
  EmployeePositionLookup,
  EmployeePositionUpdate,
  EmployeeUpdate,
  Examination,
  ExaminationCreate,
  ExaminationRecord,
  ExaminationRecordCreate,
  ExaminationRecordListParams,
  ExaminationRecordUpdate,
  ExaminationUpdate,
  ExaminationsReportParams,
  GenerateWeekPlansRequest,
  IdentityFullSyncResponse,
  IdentityStatusResponse,
  LeaveAccountUpdate,
  LeaveBalance,
  LeaveDecision,
  LeaveRequest,
  LeaveRequestCreate,
  LeaveRequestListParams,
  GenerateWeekPlansResponse,
  JobDescription,
  JobDescriptionCreate,
  JobDescriptionUpdate,
  OrgChart,
  OrgUnit,
  OrgUnitCreate,
  OrgUnitUpdate,
  Page,
  PageParams,
  PhoneNumber,
  PhoneNumberCreate,
  PhoneNumberUpdate,
  PlannedEmployee,
  PlannedEmployeesParams,
  Position,
  PositionCreate,
  PositionExamination,
  PositionExaminationCreate,
  PositionExaminationListParams,
  PositionExaminationUpdate,
  PositionUpdate,
  ReassignRequest,
  ReassignResponse,
  ReportAbsenceRequest,
  ReportAbsenceResponse,
  RolloutRequest,
  RolloutResponse,
  RotationAnchorRequest,
  RotationAnchorResponse,
  RotationStatus,
  SendExaminationsReportResponse,
  Shift,
  ShiftCreate,
  SickLeave,
  SickLeaveClose,
  SickLeaveCreate,
  SickLeaveListParams,
  SickLeavePage,
  VacationAdjustmentPlan,
  VacationAdjustmentRequest,
  ReportDuringVacationRequest,
  ReportDuringVacationResult,
  ShiftModel,
  ShiftModelCreate,
  ShiftModelUpdate,
  ShiftPlanCreateRequest,
  ShiftPlanCreateResponse,
  ShiftPlanEntry,
  ShiftPlanEntryUpdate,
  ShiftPlanListParams,
  ShiftPlanPreview,
  ShiftPlanRangeParams,
  ShiftPlanRetractResponse,
  ShiftUpdate,
  SurchargesReportParams,
  SurchargesReportResponse,
  SwapCandidate,
  SwapRequest,
  SwapResponse,
  SyncRequiredResponse,
  Team,
  TeamCreate,
  TeamMember,
  TeamOverview,
  TeamUpdate,
  TrainingCourse,
  TrainingCourseCreate,
  TrainingCourseUpdate,
  TrainingRecord,
  TrainingRecordCreate,
  TrainingRecordListParams,
  TrainingRecordUpdate,
  WeekPlanListParams,
  WeekPlanRow,
  WorkInstruction,
  WorkInstructionCreate,
  WorkInstructionUpdate,
  Workplace,
  WorkplaceCreate,
  WorkplaceInstruction,
  WorkplaceInstructionCreate,
  WorkplaceInstructionListParams,
  WorkplaceUpdate,
} from "@/types"

// BASE_URL endet mit "/" — Root: "/api/v1", Pfad-Betrieb: "/personal/api/v1"
export const API_BASE_URL = `${import.meta.env.BASE_URL}api/v1`

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Request-Interceptor: Bearer-Token (Logto) bzw. X-Dev-User (Dev-Modus)
client.interceptors.request.use(async (config) => {
  if (AUTH_MODE === "dev") {
    config.headers["X-Dev-User"] = JSON.stringify(getDevUser())
    return config
  }
  const token = await getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response-Interceptor: kein Auto-Logout bei 401 (Redirect-Loops vermeiden);
// Auth-State verwaltet der AuthProvider. Im Logto-Modus stoesst ein 401
// (abgelaufene Session, Refresh fehlgeschlagen) genau EINEN Re-Login-Redirect
// an (Schleifenschutz in triggerRelogin, store/auth.ts).
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      triggerRelogin()
    }
    return Promise.reject(error)
  }
)

/** Deutsche Fehlermeldung aus einer Axios/FastAPI-Fehlerantwort extrahieren */
export function apiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === "string") return detail
    // 409-Konflikte der Abwesenheiten: detail = { detail, conflicts } (PRD §4.3)
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const inner = (detail as { detail?: unknown }).detail
      if (typeof inner === "string") return inner
    }
    if (error.response?.status === 403) return "Keine Berechtigung."
    if (error.response?.status === 401) return "Nicht angemeldet."
    return error.message
  }
  return "Unbekannter Fehler."
}

/** HTTP-Statuscode einer fehlgeschlagenen Anfrage (sonst null) */
export function apiStatus(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null
}

/** Konfliktliste aus einer 409-Antwort der Abwesenheiten extrahieren (sonst null) */
export function apiConflicts(error: unknown): AbsenceConflict[] | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null
  const detail = (error.response.data as { detail?: unknown } | undefined)?.detail
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const conflicts = (detail as { conflicts?: unknown }).conflicts
    if (Array.isArray(conflicts)) return conflicts as AbsenceConflict[]
  }
  return null
}

export { client }

/**
 * Datei-Download ueber den Axios-Client (Blob) — noetig, weil Auth per Header
 * laeuft (Bearer/X-Dev-User); window.open wuerde den Header verlieren.
 * Dateiname kommt aus Content-Disposition, sonst fallbackName.
 */
async function downloadViaClient(
  url: string,
  params: Record<string, unknown> | undefined,
  fallbackName: string
): Promise<void> {
  const res = await client.get<Blob>(url, { params, responseType: "blob" })
  const disposition = String(res.headers["content-disposition"] ?? "")
  const match = /filename="?([^";]+)"?/.exec(disposition)
  const name = match?.[1] ?? fallbackName
  const href = URL.createObjectURL(res.data)
  const anchor = document.createElement("a")
  anchor.href = href
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(href)
}

/** Multipart-Upload einer einzelnen Datei (Feldname "file") */
function uploadFile<T>(url: string, file: File): Promise<T> {
  const form = new FormData()
  form.append("file", file)
  return client
    .post<T>(url, form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data)
}

// =============================================================================
// DOMAIN-NAMESPACES
// Werden in den Fach-Phasen befuellt, z. B.:
//   employees: {
//     list: (p: EmployeeFilter) =>
//       client.get<Page<Employee>>("/employees", { params: p }).then((r) => r.data),
//     get: (id: string) =>
//       client.get<Employee>(`/employees/${id}`).then((r) => r.data),
//   }
// =============================================================================

export const api = {
  // ---------------------------------------------------------------------------
  // Mitarbeiter / Personalakte (endpoints/employees.py)
  // ---------------------------------------------------------------------------
  employees: {
    list: (params?: EmployeeListParams) =>
      client.get<Page<EmployeeListItem>>("/employees", { params }).then((r) => r.data),
    get: (id: string) => client.get<EmployeeDetail>(`/employees/${id}`).then((r) => r.data),
    create: (body: EmployeeCreate) =>
      client.post<EmployeeDetail>("/employees", body).then((r) => r.data),
    update: (id: string, body: EmployeeUpdate) =>
      client.patch<EmployeeDetail>(`/employees/${id}`, body).then((r) => r.data),
    /** Soft-Delete (Gate: loeschen) */
    softDelete: (id: string) => client.delete<void>(`/employees/${id}`).then(() => undefined),
    phoneNumbers: {
      list: (employeeId: string) =>
        client
          .get<PhoneNumber[]>(`/employees/${employeeId}/phone-numbers`)
          .then((r) => r.data),
      create: (employeeId: string, body: PhoneNumberCreate) =>
        client
          .post<PhoneNumber>(`/employees/${employeeId}/phone-numbers`, body)
          .then((r) => r.data),
      update: (employeeId: string, id: string, body: PhoneNumberUpdate) =>
        client
          .patch<PhoneNumber>(`/employees/${employeeId}/phone-numbers/${id}`, body)
          .then((r) => r.data),
      remove: (employeeId: string, id: string) =>
        client
          .delete<void>(`/employees/${employeeId}/phone-numbers/${id}`)
          .then(() => undefined),
    },
    emails: {
      list: (employeeId: string) =>
        client.get<EmployeeEmail[]>(`/employees/${employeeId}/emails`).then((r) => r.data),
      create: (employeeId: string, body: EmployeeEmailCreate) =>
        client.post<EmployeeEmail>(`/employees/${employeeId}/emails`, body).then((r) => r.data),
      update: (employeeId: string, id: string, body: EmployeeEmailUpdate) =>
        client
          .patch<EmployeeEmail>(`/employees/${employeeId}/emails/${id}`, body)
          .then((r) => r.data),
      remove: (employeeId: string, id: string) =>
        client.delete<void>(`/employees/${employeeId}/emails/${id}`).then(() => undefined),
    },
    /** G-2: macht die Adresse transaktional zur einzigen freigegebenen */
    setPrimaryEmail: (employeeId: string, emailId: string) =>
      client
        .post<EmployeeEmail>(`/employees/${employeeId}/emails/${emailId}/set-primary`)
        .then((r) => r.data),
    addresses: {
      list: (employeeId: string) =>
        client.get<EmployeeAddress[]>(`/employees/${employeeId}/addresses`).then((r) => r.data),
      create: (employeeId: string, body: EmployeeAddressCreate) =>
        client
          .post<EmployeeAddress>(`/employees/${employeeId}/addresses`, body)
          .then((r) => r.data),
      update: (employeeId: string, id: string, body: EmployeeAddressUpdate) =>
        client
          .patch<EmployeeAddress>(`/employees/${employeeId}/addresses/${id}`, body)
          .then((r) => r.data),
      remove: (employeeId: string, id: string) =>
        client.delete<void>(`/employees/${employeeId}/addresses/${id}`).then(() => undefined),
    },
    positions: {
      list: (employeeId: string) =>
        client
          .get<EmployeePositionEntry[]>(`/employees/${employeeId}/positions`)
          .then((r) => r.data),
      create: (employeeId: string, body: EmployeePositionCreate) =>
        client
          .post<EmployeePositionEntry>(`/employees/${employeeId}/positions`, body)
          .then((r) => r.data),
      update: (employeeId: string, id: string, body: EmployeePositionUpdate) =>
        client
          .patch<EmployeePositionEntry>(`/employees/${employeeId}/positions/${id}`, body)
          .then((r) => r.data),
      remove: (employeeId: string, id: string) =>
        client.delete<void>(`/employees/${employeeId}/positions/${id}`).then(() => undefined),
    },
  },
  // ---------------------------------------------------------------------------
  // Anwesenheitsboard (endpoints/attendance.py, G-10…G-15)
  // ---------------------------------------------------------------------------
  attendance: {
    /** G-14: drei Listen + Vortags-Ueberhang; Default-Stichtag heute */
    board: (date?: string) =>
      client
        .get<AttendanceBoard>("/attendance/board", { params: date ? { date } : undefined })
        .then((r) => r.data),
    /** G-11: Einbuchen (Abwesenheits-Guard → 409 mit Grund, ausser RUECKRUF) */
    checkIn: (body: CheckInRequest) =>
      client.post<AttendanceRecord>("/attendance/check-in", body).then((r) => r.data),
    /** G-12: Ausbuchen (Stunden 1–12, Zulagen einfrieren) */
    checkOut: (body: CheckOutRequest) =>
      client.post<AttendanceRecord>("/attendance/check-out", body).then((r) => r.data),
    /** G-10: Statusmeldung → Abwesenheits-Upsert am Planzeilen-Tag */
    reportAbsence: (body: ReportAbsenceRequest) =>
      client
        .post<ReportAbsenceResponse>("/attendance/report-absence", body)
        .then((r) => r.data),
    /** Recherche (Sichtbarkeits-Policy PRD §2) */
    list: (params?: AttendanceListParams) =>
      client.get<Page<AttendanceRecord>>("/attendance", { params }).then((r) => r.data),
    /** Admin-Korrektur (personal_schreiben): Re-Freeze + Journal */
    adminUpdate: (id: string, body: AttendanceAdminUpdate) =>
      client.patch<AttendanceRecord>(`/attendance/${id}`, body).then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Schichtplanung (endpoints/planning.py, G-13/G-17/G-18/G-22/G-37)
  // ---------------------------------------------------------------------------
  planning: {
    weekPlans: {
      /** Wochenplan-Matrix flach (Default-Seitengroesse 200 = komplettes Jahr) */
      list: (params: WeekPlanListParams) =>
        client.get<Page<WeekPlanRow>>("/week-plans", { params }).then((r) => r.data),
      /** Phase 1 (G-17): Rotations-Generator */
      generate: (body: GenerateWeekPlansRequest) =>
        client
          .post<GenerateWeekPlansResponse>("/week-plans/generate", body)
          .then((r) => r.data),
    },
    /** Read-only-Vorschau (U-32c): Rotations-Matrix + Zaehl-Prognose, speichert NICHTS */
    preview: (params: ShiftPlanRangeParams) =>
      client.get<ShiftPlanPreview>("/week-plans/preview", { params }).then((r) => r.data),
    /** Kombi (U-32c): Phase 1 + Phase 2 in EINEM Request/EINER Transaktion (G-17/G-18/G-23) */
    createShiftPlan: (body: ShiftPlanCreateRequest) =>
      client.post<ShiftPlanCreateResponse>("/shift-plan/create", body).then((r) => r.data),
    /** G-42: Kontrollierte Ruecknahme — loescht Planzeilen + Wochenplaene des Modells
     *  im KW-Bereich; Zeilen mit gebuchter Anwesenheit bleiben stehen → blocked */
    retract: (params: ShiftPlanRangeParams) =>
      client
        .delete<ShiftPlanRetractResponse>("/shift-plan", { params })
        .then((r) => r.data),
    /**
     * G-17 Startbelegung/Rotations-Anker:
     *  - rotationStatus: Vorschalt-Status (hasAnchor? Teams/Schichten? Vorschlag-KW?)
     *  - setRotationAnchor: Startbelegung festlegen (422 = keine gueltige Phase, 409 = Plaene existieren)
     */
    rotation: {
      status: (modelId: number, year?: number, week?: number) =>
        client
          .get<RotationStatus>(`/shift-models/${modelId}/rotation-status`, {
            params: { year, week },
          })
          .then((r) => r.data),
      setAnchor: (modelId: number, body: RotationAnchorRequest) =>
        client
          .post<RotationAnchorResponse>(`/shift-models/${modelId}/rotation-anchor`, body)
          .then((r) => r.data),
    },
    /** Planzeilen mit Presence-Filter (from/to/shift/employee/presence) */
    shiftPlan: (params?: ShiftPlanListParams) =>
      client.get<Page<ShiftPlanEntry>>("/shift-plan", { params }).then((r) => r.data),
    /** Phase 2 (G-18): Ausrollung ueber alle 7 Kalendertage der ISO-Woche */
    rollout: (body: RolloutRequest) =>
      client.post<RolloutResponse>("/shift-plan/rollout", body).then((r) => r.data),
    /** G-13: Schicht/Stelle einer Planzeile aendern (Abwesenheits-Guard → 409) */
    updateEntry: (id: string, body: ShiftPlanEntryUpdate) =>
      client.patch<ShiftPlanEntry>(`/shift-plan/${id}`, body).then((r) => r.data),
    /** G-22: Umplanen/Ersatz — Konfliktliste statt Ninox-Alert-Ketten */
    reassign: (body: ReassignRequest) =>
      client.post<ReassignResponse>("/shift-plan/reassign", body).then((r) => r.data),
    /** G-37: DISTINCT Mitarbeiter mit Planzeilen in Schicht X im Zeitraum */
    plannedEmployees: (params: PlannedEmployeesParams) =>
      client
        .get<PlannedEmployee[]>("/shift-plan/planned-employees", { params })
        .then((r) => r.data),
    /** G-41: Tauschbare Gegenstücke einer Planzeile (selber Tag, andere Schicht) */
    swapCandidates: (entryId: string) =>
      client
        .get<SwapCandidate[]>("/shift-plan/swap-candidates", { params: { entryId } })
        .then((r) => r.data),
    /** G-41: Atomarer Schichttausch zweier Planzeilen (Abwesenheits-Guard → 409) */
    swapShifts: (body: SwapRequest) =>
      client.post<SwapResponse>("/shift-plan/swap", body).then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Abwesenheiten (endpoints/absences.py, G-20/G-21)
  // ---------------------------------------------------------------------------
  absences: {
    list: (params?: AbsenceListParams) =>
      client.get<Page<Absence>>("/absences", { params }).then((r) => r.data),
    get: (id: string) => client.get<Absence>(`/absences/${id}`).then((r) => r.data),
    create: (body: AbsenceCreate) =>
      client.post<Absence>("/absences", body).then((r) => r.data),
    update: (id: string, body: AbsenceUpdate) =>
      client.patch<Absence>(`/absences/${id}`, body).then((r) => r.data),
    remove: (id: string) => client.delete<void>(`/absences/${id}`).then(() => undefined),
    /** Massenbuchung (G-20): EIN Zeitraum, gezaehlt werden Betriebstage */
    bulkCreate: (body: BulkAbsenceRequest) =>
      client.post<BulkAbsenceResponse>("/absences/bulk", body).then((r) => r.data),
    /** Massen-Loeschung (G-21): alle Abwesenheiten, die den Zeitraum schneiden */
    bulkDelete: (params: BulkDeleteParams) =>
      client
        .delete<BulkDeleteResponse>("/absences/bulk", { params })
        .then((r) => r.data),
    /** Betriebsurlaub auf Mitarbeiter anwenden (idempotente Ein-Klick-Massenaktion) */
    applyClosure: (body: ApplyClosureRequest) =>
      client.post<ApplyClosureResponse>("/absences/apply-closure", body).then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Urlaub: Antraege + Konten + Team-Uebersicht (endpoints/leave.py, AP 3.2/3.3)
  // ---------------------------------------------------------------------------
  leave: {
    requests: {
      list: (params?: LeaveRequestListParams) =>
        client.get<Page<LeaveRequest>>("/leave-requests", { params }).then((r) => r.data),
      /** Antrag stellen (Self-Service; 409 = Ueberlappung, 422 = Saldo) */
      create: (body: LeaveRequestCreate) =>
        client.post<LeaveRequest>("/leave-requests", body).then((r) => r.data),
      /** PENDING → APPROVED (Gate personal_schreiben) */
      approve: (id: string, body?: LeaveDecision) =>
        client.post<LeaveRequest>(`/leave-requests/${id}/approve`, body).then((r) => r.data),
      /** PENDING → REJECTED (Gate personal_schreiben, Begruendung Pflicht) */
      reject: (id: string, note: string) =>
        client.post<LeaveRequest>(`/leave-requests/${id}/reject`, { note }).then((r) => r.data),
      /** Storno: PENDING sofort; APPROVED → Anfrage bzw. Bestaetigung */
      cancel: (id: string, body?: LeaveDecision) =>
        client.post<LeaveRequest>(`/leave-requests/${id}/cancel`, body).then((r) => r.data),
    },
    account: {
      /** Konto + berechneter Saldo (Default-Anlage on-demand, Anspruch 30) */
      get: (employeeId: string, year: number, referenceDate?: string) =>
        client
          .get<LeaveBalance>(`/leave-accounts/${employeeId}/${year}`, {
            params: referenceDate ? { referenceDate } : undefined,
          })
          .then((r) => r.data),
      /** Konto pflegen (Gate personal_schreiben) */
      update: (employeeId: string, year: number, body: LeaveAccountUpdate) =>
        client
          .patch<LeaveBalance>(`/leave-accounts/${employeeId}/${year}`, body)
          .then((r) => r.data),
    },
    /** Alle sichtbaren Mitarbeiter × Tage mit Abwesenheits-Grund (Kalender-UI) */
    teamOverview: (year: number, month: number) =>
      client
        .get<TeamOverview>("/leave-requests/team-overview", { params: { year, month } })
        .then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Krankmeldungen (endpoints/sick_leaves.py, AP 3.4)
  // ---------------------------------------------------------------------------
  sickLeaves: {
    /** Liste mit Filtern; bei employee+year zusaetzlich Bradford-Faktor */
    list: (params?: SickLeaveListParams) =>
      client.get<SickLeavePage>("/sick-leaves", { params }).then((r) => r.data),
    /** Krankmeldung: erzeugt absence (KRANK, BOOKED) + sick_leave */
    create: (body: SickLeaveCreate) =>
      client.post<SickLeave>("/sick-leaves", body).then((r) => r.data),
    /** AU-Bescheinigung als multipart/form-data hochladen */
    uploadCertificate: (id: string, file: File) => {
      const form = new FormData()
      form.append("file", file)
      return client
        .post<SickLeave>(`/sick-leaves/${id}/certificate`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data)
    },
    /** Gesundmeldung (Gate personal_schreiben) — optional actualEndDate */
    close: (id: string, body?: SickLeaveClose) =>
      client.post<SickLeave>(`/sick-leaves/${id}/close`, body).then((r) => r.data),
    /** Vorschau (BUrlG §9): welche Urlaube werden aufgetrennt, wie viele Tage zurueck */
    previewVacationAdjustment: (body: VacationAdjustmentRequest) =>
      client
        .post<VacationAdjustmentPlan>("/sick-leaves/preview-vacation-adjustment", body)
        .then((r) => r.data),
    /** Ein-Klick (BUrlG §9): Urlaub auftrennen + Krankheit buchen in EINER Transaktion */
    reportDuringVacation: (body: ReportDuringVacationRequest) =>
      client
        .post<ReportDuringVacationResult>("/sick-leaves/report-during-vacation", body)
        .then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Schulungen (endpoints/trainings.py, G-5…G-8)
  // ---------------------------------------------------------------------------
  trainings: {
    courses: {
      list: (params?: PageParams) =>
        client.get<Page<TrainingCourse>>("/training-courses", { params }).then((r) => r.data),
      get: (id: string) =>
        client.get<TrainingCourse>(`/training-courses/${id}`).then((r) => r.data),
      create: (body: TrainingCourseCreate) =>
        client.post<TrainingCourse>("/training-courses", body).then((r) => r.data),
      update: (id: string, body: TrainingCourseUpdate) =>
        client.patch<TrainingCourse>(`/training-courses/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/training-courses/${id}`).then(() => undefined),
    },
    records: {
      list: (params?: TrainingRecordListParams) =>
        client.get<Page<TrainingRecord>>("/training-records", { params }).then((r) => r.data),
      get: (id: string) =>
        client.get<TrainingRecord>(`/training-records/${id}`).then((r) => r.data),
      create: (body: TrainingRecordCreate) =>
        client.post<TrainingRecord>("/training-records", body).then((r) => r.data),
      update: (id: string, body: TrainingRecordUpdate) =>
        client.patch<TrainingRecord>(`/training-records/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/training-records/${id}`).then(() => undefined),
      /** G-8: alten Nachweis entwerten, leeren Folgedatensatz zurueckgeben */
      renew: (id: string) =>
        client.post<TrainingRecord>(`/training-records/${id}/renew`).then((r) => r.data),
      /** Nachweis-Upload (multipart) */
      uploadCertificate: (id: string, file: File) =>
        uploadFile<TrainingRecord>(`/training-records/${id}/certificate`, file),
    },
  },
  // ---------------------------------------------------------------------------
  // Beauftragungen (endpoints/assignments.py, G-9)
  // ---------------------------------------------------------------------------
  assignments: {
    list: (params?: AssignmentListParams) =>
      client.get<Page<Assignment>>("/assignments", { params }).then((r) => r.data),
    get: (id: string) => client.get<Assignment>(`/assignments/${id}`).then((r) => r.data),
    create: (body: AssignmentCreate) =>
      client.post<Assignment>("/assignments", body).then((r) => r.data),
    update: (id: string, body: AssignmentUpdate) =>
      client.patch<Assignment>(`/assignments/${id}`, body).then((r) => r.data),
    remove: (id: string) => client.delete<void>(`/assignments/${id}`).then(() => undefined),
  },
  // ---------------------------------------------------------------------------
  // Vorsorge / AMV (endpoints/examinations.py, G-24…G-26)
  // ---------------------------------------------------------------------------
  examinations: {
    catalog: {
      list: (params?: PageParams) =>
        client.get<Page<Examination>>("/examinations", { params }).then((r) => r.data),
      get: (id: string) => client.get<Examination>(`/examinations/${id}`).then((r) => r.data),
      create: (body: ExaminationCreate) =>
        client.post<Examination>("/examinations", body).then((r) => r.data),
      update: (id: string, body: ExaminationUpdate) =>
        client.patch<Examination>(`/examinations/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/examinations/${id}`).then(() => undefined),
    },
    records: {
      list: (params?: ExaminationRecordListParams) =>
        client
          .get<Page<ExaminationRecord>>("/examination-records", { params })
          .then((r) => r.data),
      get: (id: string) =>
        client.get<ExaminationRecord>(`/examination-records/${id}`).then((r) => r.data),
      /** Einzel-Anlage — UNIQUE (Mitarbeiter, Untersuchung) → 409 (G-25) */
      create: (body: ExaminationRecordCreate) =>
        client.post<ExaminationRecord>("/examination-records", body).then((r) => r.data),
      update: (id: string, body: ExaminationRecordUpdate) =>
        client.patch<ExaminationRecord>(`/examination-records/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/examination-records/${id}`).then(() => undefined),
    },
    positionExaminations: {
      list: (params?: PositionExaminationListParams) =>
        client
          .get<Page<PositionExamination>>("/position-examinations", { params })
          .then((r) => r.data),
      create: (body: PositionExaminationCreate) =>
        client.post<PositionExamination>("/position-examinations", body).then((r) => r.data),
      update: (id: string, body: PositionExaminationUpdate) =>
        client
          .patch<PositionExamination>(`/position-examinations/${id}`, body)
          .then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/position-examinations/${id}`).then(() => undefined),
    },
    /** G-24: Soll-Liste — Pflicht-Untersuchungen aus den Stellenzuordnungen */
    requiredFor: (employeeId: string) =>
      client
        .get<Examination[]>(`/employees/${employeeId}/required-examinations`)
        .then((r) => r.data),
    /** G-25: fehlende Pflicht-Untersuchungen idempotent anlegen */
    syncRequired: (employeeId: string) =>
      client
        .post<SyncRequiredResponse>(`/employees/${employeeId}/examinations/sync-required`)
        .then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Dokumente (endpoints/documents.py)
  // ---------------------------------------------------------------------------
  documents: {
    list: (params?: DocumentListParams) =>
      client.get<Page<DocumentItem>>("/documents", { params }).then((r) => r.data),
    get: (id: string) => client.get<DocumentItem>(`/documents/${id}`).then((r) => r.data),
    create: (body: DocumentCreate) =>
      client.post<DocumentItem>("/documents", body).then((r) => r.data),
    update: (id: string, body: DocumentUpdate) =>
      client.patch<DocumentItem>(`/documents/${id}`, body).then((r) => r.data),
    remove: (id: string) => client.delete<void>(`/documents/${id}`).then(() => undefined),
    /** Datei-Upload (multipart) → STORAGE_PATH/documents/{id}/ */
    uploadFile: (id: string, file: File) =>
      uploadFile<DocumentItem>(`/documents/${id}/file`, file),
    /** Datei-Download (Blob, Header-Auth) */
    downloadFile: (id: string, fallbackName = "dokument.pdf") =>
      downloadViaClient(`/documents/${id}/file`, undefined, fallbackName),
    workInstructions: {
      list: (params?: PageParams) =>
        client.get<Page<WorkInstruction>>("/work-instructions", { params }).then((r) => r.data),
      get: (id: string) =>
        client.get<WorkInstruction>(`/work-instructions/${id}`).then((r) => r.data),
      create: (body: WorkInstructionCreate) =>
        client.post<WorkInstruction>("/work-instructions", body).then((r) => r.data),
      update: (id: string, body: WorkInstructionUpdate) =>
        client.patch<WorkInstruction>(`/work-instructions/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/work-instructions/${id}`).then(() => undefined),
    },
    workplaceInstructions: {
      list: (params?: WorkplaceInstructionListParams) =>
        client
          .get<Page<WorkplaceInstruction>>("/workplace-instructions", { params })
          .then((r) => r.data),
      add: (body: WorkplaceInstructionCreate) =>
        client.post<WorkplaceInstruction>("/workplace-instructions", body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/workplace-instructions/${id}`).then(() => undefined),
    },
  },
  // ---------------------------------------------------------------------------
  // Reports (endpoints/reports.py, G-16/G-26)
  // ---------------------------------------------------------------------------
  reports: {
    attendance: (params?: AttendanceReportParams) =>
      client
        .get<AttendanceReportResponse>("/reports/attendance", { params })
        .then((r) => r.data),
    /** XLSX-Export als Blob-Download (Header-Auth) */
    downloadAttendanceXlsx: (params?: AttendanceReportParams) =>
      downloadViaClient(
        "/reports/attendance.xlsx",
        params as Record<string, unknown> | undefined,
        "Anwesenheiten.xlsx"
      ),
    surcharges: (params: SurchargesReportParams) =>
      client
        .get<SurchargesReportResponse>("/reports/surcharges", { params })
        .then((r) => r.data),
    /** G-26: AMV-Excel-Export als Blob-Download */
    downloadExaminationsXlsx: (params?: ExaminationsReportParams) =>
      downloadViaClient(
        "/reports/examinations.xlsx",
        params as Record<string, unknown> | undefined,
        "AMV.xlsx"
      ),
    /** G-26: AMV-Report frisch erzeugen + per Mail versenden (202/queued) */
    sendExaminationsReport: (recipients: string[]) =>
      client
        .post<SendExaminationsReportResponse>("/reports/examinations/send", { recipients })
        .then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Dashboard (endpoints/dashboard.py) — alle Widget-Daten in EINEM Request
  // ---------------------------------------------------------------------------
  dashboard: {
    get: () => client.get<DashboardResponse>("/dashboard").then((r) => r.data),
  },
  // ---------------------------------------------------------------------------
  // Identity-Sync (endpoints/identity.py, G-28) — nur admin
  // ---------------------------------------------------------------------------
  identity: {
    sync: () => client.post<IdentityFullSyncResponse>("/identity/sync").then((r) => r.data),
    status: () => client.get<IdentityStatusResponse>("/identity/status").then((r) => r.data),
  },

  // ---------------------------------------------------------------------------
  // Betriebskalender (endpoints/calendar.py)
  // ---------------------------------------------------------------------------
  calendar: {
    /** 365/366 berechnete DayInfos fuer das Jahres-Grid */
    year: (year: number) =>
      client.get<CalendarYear>(`/calendar/year/${year}`).then((r) => r.data),
    /** DayInfos fuer einen Zeitraum ("YYYY-MM-DD") */
    dayInfos: (from: string, to: string) =>
      client.get<DayInfos>("/calendar/day-infos", { params: { from, to } }).then((r) => r.data),
    entries: {
      list: (params?: CalendarEntryListParams) =>
        client.get<Page<CalendarEntry>>("/calendar/entries", { params }).then((r) => r.data),
      create: (body: CalendarEntryCreate) =>
        client.post<CalendarEntry>("/calendar/entries", body).then((r) => r.data),
      update: (id: string, body: CalendarEntryUpdate) =>
        client.patch<CalendarEntry>(`/calendar/entries/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/calendar/entries/${id}`).then(() => undefined),
    },
    /** Betriebsurlaubs-Zeitraum als EIN Range-Entry setzen/entfernen (G-33) */
    closureRange: (body: ClosureRangeRequest) =>
      client.post<ClosureRangeResult>("/calendar/closure-range", body).then((r) => r.data),
  },

  // ---------------------------------------------------------------------------
  // Organisation + Stammdaten (endpoints/organization.py, shift_master.py,
  // assignment_types.py)
  // ---------------------------------------------------------------------------
  organization: {
    workplaces: {
      list: (params?: PageParams) =>
        client.get<Page<Workplace>>("/workplaces", { params }).then((r) => r.data),
      get: (id: string) => client.get<Workplace>(`/workplaces/${id}`).then((r) => r.data),
      create: (body: WorkplaceCreate) =>
        client.post<Workplace>("/workplaces", body).then((r) => r.data),
      update: (id: string, body: WorkplaceUpdate) =>
        client.patch<Workplace>(`/workplaces/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/workplaces/${id}`).then(() => undefined),
    },
    positions: {
      list: (params?: PageParams) =>
        client.get<Page<Position>>("/positions", { params }).then((r) => r.data),
      get: (id: string) => client.get<Position>(`/positions/${id}`).then((r) => r.data),
      create: (body: PositionCreate) =>
        client.post<Position>("/positions", body).then((r) => r.data),
      update: (id: string, body: PositionUpdate) =>
        client.patch<Position>(`/positions/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/positions/${id}`).then(() => undefined),
    },
    /** Lookup Stellenzuordnungen (Mitarbeiter↔Stelle) fuer Auswahlfelder, z. B. Team-Besetzung */
    employeePositions: {
      list: (params?: PageParams & { nurFreie?: boolean }) =>
        client
          .get<Page<EmployeePositionLookup>>("/employee-positions", { params })
          .then((r) => r.data),
    },
    orgUnits: {
      list: (params?: PageParams) =>
        client.get<Page<OrgUnit>>("/org-units", { params }).then((r) => r.data),
      get: (id: string) => client.get<OrgUnit>(`/org-units/${id}`).then((r) => r.data),
      create: (body: OrgUnitCreate) =>
        client.post<OrgUnit>("/org-units", body).then((r) => r.data),
      update: (id: string, body: OrgUnitUpdate) =>
        client.patch<OrgUnit>(`/org-units/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/org-units/${id}`).then(() => undefined),
    },
    /** Rekursiver Organigramm-Baum mit berechneter Ebene (G-27) */
    orgChart: () => client.get<OrgChart>("/org-chart").then((r) => r.data),
    costCenters: {
      list: (params?: PageParams) =>
        client.get<Page<CostCenter>>("/cost-centers", { params }).then((r) => r.data),
      get: (id: string) => client.get<CostCenter>(`/cost-centers/${id}`).then((r) => r.data),
      create: (body: CostCenterCreate) =>
        client.post<CostCenter>("/cost-centers", body).then((r) => r.data),
      update: (id: string, body: CostCenterUpdate) =>
        client.patch<CostCenter>(`/cost-centers/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/cost-centers/${id}`).then(() => undefined),
    },
    jobDescriptions: {
      list: (params?: PageParams) =>
        client.get<Page<JobDescription>>("/job-descriptions", { params }).then((r) => r.data),
      get: (id: string) =>
        client.get<JobDescription>(`/job-descriptions/${id}`).then((r) => r.data),
      create: (body: JobDescriptionCreate) =>
        client.post<JobDescription>("/job-descriptions", body).then((r) => r.data),
      update: (id: string, body: JobDescriptionUpdate) =>
        client.patch<JobDescription>(`/job-descriptions/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/job-descriptions/${id}`).then(() => undefined),
    },
    shiftModels: {
      list: (params?: PageParams) =>
        client.get<Page<ShiftModel>>("/shift-models", { params }).then((r) => r.data),
      get: (id: string) => client.get<ShiftModel>(`/shift-models/${id}`).then((r) => r.data),
      create: (body: ShiftModelCreate) =>
        client.post<ShiftModel>("/shift-models", body).then((r) => r.data),
      update: (id: string, body: ShiftModelUpdate) =>
        client.patch<ShiftModel>(`/shift-models/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/shift-models/${id}`).then(() => undefined),
    },
    shifts: {
      list: (params?: { page?: number; size?: number; shiftModelId?: number }) =>
        client.get<Page<Shift>>("/shifts", { params }).then((r) => r.data),
      get: (id: string) => client.get<Shift>(`/shifts/${id}`).then((r) => r.data),
      create: (body: ShiftCreate) => client.post<Shift>("/shifts", body).then((r) => r.data),
      update: (id: string, body: ShiftUpdate) =>
        client.patch<Shift>(`/shifts/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/shifts/${id}`).then(() => undefined),
    },
    teams: {
      list: (params?: PageParams) =>
        client.get<Page<Team>>("/teams", { params }).then((r) => r.data),
      get: (id: string) => client.get<Team>(`/teams/${id}`).then((r) => r.data),
      create: (body: TeamCreate) => client.post<Team>("/teams", body).then((r) => r.data),
      update: (id: string, body: TeamUpdate) =>
        client.patch<Team>(`/teams/${id}`, body).then((r) => r.data),
      remove: (id: string) => client.delete<void>(`/teams/${id}`).then(() => undefined),
      /** Team-Besetzung: Ziel ist die STELLENZUORDNUNG (employee_positions, PRD §3.2) */
      members: {
        list: (teamId: string) =>
          client.get<TeamMember[]>(`/teams/${teamId}/members`).then((r) => r.data),
        add: (teamId: string, employeePositionId: number) =>
          client
            .post<TeamMember>(`/teams/${teamId}/members`, { employeePositionId })
            .then((r) => r.data),
        remove: (teamId: string, memberId: string) =>
          client.delete<void>(`/teams/${teamId}/members/${memberId}`).then(() => undefined),
      },
    },
    assignmentTypes: {
      list: (params?: PageParams) =>
        client.get<Page<AssignmentType>>("/assignment-types", { params }).then((r) => r.data),
      get: (id: string) =>
        client.get<AssignmentType>(`/assignment-types/${id}`).then((r) => r.data),
      create: (body: AssignmentTypeCreate) =>
        client.post<AssignmentType>("/assignment-types", body).then((r) => r.data),
      update: (id: string, body: AssignmentTypeUpdate) =>
        client.patch<AssignmentType>(`/assignment-types/${id}`, body).then((r) => r.data),
      remove: (id: string) =>
        client.delete<void>(`/assignment-types/${id}`).then(() => undefined),
    },
  },
}
